import Menu from './menu';

import { Opcodes, Packets } from '@kaetram/common/network';

import type { PartyPacket } from '@kaetram/common/types/messages/outgoing';
import type Game from '../game';

export interface PartyMemberStats {
    username: string;
    level?: number;
    hp?: number;
    maxHp?: number;
    mana?: number;
    maxMana?: number;
}

export default class Party extends Menu {
    private memberList: HTMLElement = document.querySelector('#party-members')!;
    private leaveBtn: HTMLButtonElement = document.querySelector('#party-leave-btn')!;
    private disbandBtn: HTMLButtonElement = document.querySelector('#party-disband-btn')!;
    private partyBody: HTMLElement = document.querySelector('#party-body')!;
    private minimizeBtn: HTMLButtonElement = document.querySelector('#party-minimize')!;
    private partyHeader: HTMLElement = document.querySelector('#party-header')!;

    // Invite modal
    private inviteModal: HTMLElement = document.querySelector('#party-invite-modal')!;
    private inviteText: HTMLElement = document.querySelector('#party-invite-text')!;
    private inviteAccept: HTMLButtonElement = document.querySelector('#party-invite-accept')!;
    private inviteDecline: HTMLButtonElement = document.querySelector('#party-invite-decline')!;

    private members: string[] = [];
    private leader = '';
    private myUsername = '';
    private stats: { [key: string]: PartyMemberStats } = {};
    private minimized = false;
    private dragging = false;
    private dragOffsetX = 0;
    private dragOffsetY = 0;

    public constructor(private game: Game) {
        super('#party-panel');

        this.leaveBtn?.addEventListener('click', () => {
            this.game.socket.send(Packets.Chat, ['/party salir']);
        });

        this.disbandBtn?.addEventListener('click', () => {
            this.game.socket.send(Packets.Chat, ['/party disolver']);
        });

        this.inviteAccept?.addEventListener('click', () => {
            this.game.socket.send(Packets.Chat, ['/party aceptar']);
            this.hideInvite();
        });

        this.inviteDecline?.addEventListener('click', () => {
            this.game.socket.send(Packets.Chat, ['/party rechazar']);
            this.hideInvite();
        });

        this.minimizeBtn?.addEventListener('click', () => this.toggleMinimize());

        this.partyHeader?.addEventListener('mousedown', (e) => {
            if ((e.target as HTMLElement).id === 'party-minimize') return;
            this.dragging = true;
            let rect = this.container.getBoundingClientRect();
            this.dragOffsetX = e.clientX - rect.left;
            this.dragOffsetY = e.clientY - rect.top;
            e.preventDefault();
        });

        document.addEventListener('mousemove', (e) => {
            if (!this.dragging) return;
            let x = e.clientX - this.dragOffsetX,
                y = e.clientY - this.dragOffsetY;
            this.container.style.left = `${Math.max(0, x)}px`;
            this.container.style.top = `${Math.max(0, y)}px`;
        });

        document.addEventListener('mouseup', () => {
            this.dragging = false;
        });
    }

    private toggleMinimize(): void {
        this.minimized = !this.minimized;
        this.partyBody.style.display = this.minimized ? 'none' : '';
        this.minimizeBtn.textContent = this.minimized ? '+' : '−';
    }

    public handle(opcode: Opcodes.Party, info: PartyPacket): void {
        this.myUsername = this.game.player.name ?? '';

        switch (opcode) {
            case Opcodes.Party.Invite: {
                this.showInvite(info.username ?? '');
                break;
            }

            case Opcodes.Party.Update: {
                this.leader = info.leader ?? info.username ?? '';
                this.members = (info.members ?? []).map((m) => m.username);
                // Seed stats from the members payload if the server sent any.
                for (let m of info.members ?? []) {
                    let s = m as PartyMemberStats;
                    if (s.hp !== undefined || s.level !== undefined) this.stats[s.username] = s;
                }
                this.refresh();
                this.show();
                break;
            }

            case Opcodes.Party.Leave:
            case Opcodes.Party.Disband: {
                this.members = [];
                this.leader = '';
                this.stats = {};
                this.refresh();
                this.hide();
                break;
            }
        }
    }

    /**
     * Applies a live stats update pushed from the server (Party.Update with
     * per-member HP/mana/level payload). Called from connection.ts when the
     * server broadcasts a member state change.
     */

    public updateMemberStats(list: PartyMemberStats[]): void {
        for (let s of list) this.stats[s.username] = { ...this.stats[s.username], ...s };
        this.refresh();
    }

    private refresh(): void {
        if (!this.memberList) return;

        this.memberList.innerHTML = '';

        let amLeader = this.leader === this.myUsername;
        this.container.classList.toggle('leader', amLeader);

        for (let name of this.members) {
            let stats = this.stats[name] ?? { username: name };
            this.memberList.append(this.buildRow(name, stats, amLeader));
        }

        if (this.leaveBtn) this.leaveBtn.hidden = amLeader;
        if (this.disbandBtn) this.disbandBtn.hidden = !amLeader;
    }

    private buildRow(name: string, stats: PartyMemberStats, amLeader: boolean): HTMLLIElement {
        let li = document.createElement('li');
        li.classList.add('party-member-row');

        let header = document.createElement('div');
        header.classList.add('party-row-header');

        let nameEl = document.createElement('span');
        nameEl.classList.add('party-name');
        nameEl.textContent = name === this.leader ? `★ ${name}` : name;

        let levelEl = document.createElement('span');
        levelEl.classList.add('party-level');
        if (stats.level !== undefined) levelEl.textContent = `Nv.${stats.level}`;

        header.append(nameEl, levelEl);

        // Kick button only visible if I'm the leader and this row isn't me.
        if (amLeader && name !== this.myUsername) {
            let kick = document.createElement('button');
            kick.type = 'button';
            kick.classList.add('party-kick');
            kick.textContent = '×';
            kick.title = 'Expulsar del grupo';
            kick.addEventListener('click', () => {
                this.game.socket.send(Packets.Chat, [`/party expulsar ${name}`]);
            });
            header.append(kick);
        }

        li.append(header);

        // Bars only shown if the server sent stats. Missing values render as empty.
        let bars = document.createElement('div');
        bars.classList.add('party-bars');
        bars.append(this.buildBar('hp', stats.hp, stats.maxHp));
        bars.append(this.buildBar('mp', stats.mana, stats.maxMana));
        li.append(bars);

        if (name === this.myUsername) li.classList.add('party-self');
        if (name === this.leader) li.classList.add('party-leader');

        return li;
    }

    private buildBar(kind: 'hp' | 'mp', value?: number, max?: number): HTMLDivElement {
        let bar = document.createElement('div');
        bar.classList.add('party-bar', kind);

        let fill = document.createElement('div');
        fill.classList.add('party-bar-fill');
        let pct = value !== undefined && max ? Math.max(0, Math.min(1, value / max)) * 100 : 0;
        fill.style.width = `${pct}%`;
        bar.append(fill);
        return bar;
    }

    private showInvite(inviter: string): void {
        if (!this.inviteModal) return;
        this.inviteText.textContent = `${inviter} te invitó a su grupo.`;
        this.inviteModal.hidden = false;
    }

    private hideInvite(): void {
        if (this.inviteModal) this.inviteModal.hidden = true;
    }
}
